import React from 'react'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import {
  SortDirectionEnum,
  TestResult,
  TestType,
  useUnverifiedManualEntryTestsData,
  Lab,
  UserPermissions,
  CompanyMemberRole,
  GroupMemberRole,
  useAuth,
  UserRole,
} from '@modmd/data'
import { useSortChangeHandler } from 'utils/helpers'
import { ROUTES } from 'internal-portal/constants/routes'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { Box, Container } from 'components/Layout'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import Button from 'components/Button'
import { COLOR } from 'theme'
import { endOfDay, startOfDay } from 'date-fns'
import { UnverifiedTestsDatalist } from 'sharedComponents/TestsDatalist/UnverifiedTestsDatalist'
import { useMutation } from '@apollo/client'
import { EDIT_TEST_RESULTS } from 'internal-portal/routes/TestDetails/operations'

type TestsProps = RouteComponentProps

type FilterState = {
  location?: string
  page?: string
  direction?: SortDirectionEnum
  sortBy?: string
  dateFrom?: string
  dateTo?: string
  memberLike?: string
  companyOrGroupLike?: string
  user?: string
  result?: TestResult | 'pending'
  resultVerified?: string
  resultVerifiedAt?: string
  resultManuallyEntered?: string
  companyId?: string
  groupId?: string
  barcode?: string
  lab?: Lab
  type?: TestType
}

const UnverifiedTestsList: React.VFC<TestsProps> = () => {
  const { getUserCompaniesWithRole, getUserGroupsWithRole, hasPermission, hasRoles } = useAuth()
  const history = useHistory()
  const [selectedTests, setSelectedTests] = React.useState<string[]>([])
  const [isVerifying, setIsVerifying] = React.useState(false)
  const searchState = useSearchState<FilterState>()

  const { companies } = getUserCompaniesWithRole({
    roles: [CompanyMemberRole.Manager, CompanyMemberRole.Owner],
  })
  const { groups } = getUserGroupsWithRole({
    roles: [GroupMemberRole.Coordinator, GroupMemberRole.Manager],
  })

  const canVerifyTests = hasRoles([UserRole.CLS])

  const getCompanyIds = () => {
    if (searchState.data.companyId) return [searchState.data.companyId]
    if (
      !hasPermission([UserPermissions.SEE_FULL_TEST_SESSIONS]) &&
      hasPermission([UserPermissions.SEE_GROUP_COMPANY_LIST_TESTS])
    )
      return companies.length ? companies.map(({ id }) => id) : undefined
    return undefined
  }

  const getGroupIds = () => {
    if (searchState.data.groupId) return [searchState.data.groupId]
    if (
      !companies.length &&
      !hasPermission([UserPermissions.SEE_FULL_TEST_SESSIONS]) &&
      hasPermission([UserPermissions.SEE_GROUP_COMPANY_LIST_TESTS])
    )
      return groups.length ? groups.map(({ id }) => id) : undefined

    return undefined
  }

  const { sorter, handleSortChange } = useSortChangeHandler()
  const testsQuery = useUnverifiedManualEntryTestsData({
    filter: {
      hasResult: searchState.data.result ? searchState.data.result !== 'pending' : undefined,
      result: Object.values(TestResult).some((result) => result === searchState.data.result)
        ? (searchState.data.result as TestResult)
        : undefined,
      resultVerifiedAt: searchState.data.resultVerifiedAt,
      memberLike: searchState.data.memberLike,
      barcode: searchState.data.barcode,
      lab: searchState.data.lab || undefined,
      type: searchState.data.type || undefined,
      location: searchState.data.location || undefined,
      companyOrGroupNameLike: searchState.data.companyOrGroupLike || undefined,
      testedBetween: [
        searchState.data.dateFrom
          ? startOfDay(new Date(searchState.data.dateFrom)).toISOString()
          : null,
        searchState.data.dateTo ? endOfDay(new Date(searchState.data.dateTo)).toISOString() : null,
      ],
      groupIds: getGroupIds(),
      companyIds: getCompanyIds(),
    },
    sorter,
    page: Number(searchState.data.page) || 1,
  })

  const handleTestSelection = React.useCallback(
    (testId: string, selected: boolean) => {
      if (!canVerifyTests) return
      if (selected) {
        setSelectedTests((prev) => [...prev, testId])
      } else {
        setSelectedTests((prev) => prev.filter((id) => id !== testId))
      }
    },
    [canVerifyTests]
  )

  const handleSelectAll = React.useCallback(
    (selected: boolean) => {
      if (!canVerifyTests) return
      if (selected) {
        const allTestIds = testsQuery.data?.tests.tests.map((test) => test.id) || []
        setSelectedTests(allTestIds)
      } else {
        setSelectedTests([])
      }
    },
    [testsQuery.data?.tests.tests, canVerifyTests]
  )

  const [editTestResults] = useMutation(EDIT_TEST_RESULTS)

  const handleVerifySelectedTests = async () => {
    if (selectedTests.length === 0) return

    setIsVerifying(true)
    try {
      for (const testId of selectedTests) {
        const testData = testsQuery.data?.tests.tests.find((test) => test.id === testId)
        if (!testData || !testData.result) {
          console.error(`Test data or result not found for test ID: ${testId}`)
          continue
        }

        const inputData = {
          testIds: [String(testId)],
          resultVerified: true,
          resultVerifiedAt: new Date().toISOString(),
          result: testData.result,
          resultManuallyEntered: testData.resultManuallyEntered,
        }

        await editTestResults({
          variables: {
            inputData,
          },
        })
      }

      await testsQuery.refetch()
      setSelectedTests([])
    } catch (error: any) {
      console.error('Error verifying tests:', error)
      if (error.graphQLErrors) {
        console.error('GraphQL errors:', error.graphQLErrors)
      }
      if (error.networkError) {
        console.error('Network error:', error.networkError)
      }
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <DashboardPage>
      <Container>
        {selectedTests.length > 0 && (
          <Box marginBottom="1rem" padding="1rem" background={COLOR.grayLight} borderRadius="4px">
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>{selectedTests.length} tests selected</Box>
              <Box display="flex" gridGap="1rem">
                {canVerifyTests && (
                  <Button
                    appearance="filled"
                    size="small"
                    onClick={handleVerifySelectedTests}
                    isFetching={isVerifying}
                    disabled={isVerifying}
                  >
                    Verify selected tests
                  </Button>
                )}
                <Button appearance="ghost" size="small" onClick={() => setSelectedTests([])}>
                  Clear selection
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        <UnverifiedTestsDatalist
          tests={testsQuery.data?.tests.tests || []}
          pagination={testsQuery.data?.tests.pagination}
          isLoading={testsQuery.loading}
          selectedTests={selectedTests}
          onTestSelection={canVerifyTests ? handleTestSelection : undefined}
          onSelectAll={canVerifyTests ? handleSelectAll : undefined}
          columnLabels={{
            location: null,
          }}
          sorter={sorter}
          onSortChange={handleSortChange}
          requestDebounceInMs={250}
          onRowClick={({ id }) => history.push(`/${ROUTES.TESTS}/${id}`)}
          onPageChange={(pageNumber) => {
            searchState.setSearchState({ page: pageNumber }, false)
          }}
          pageLength={testsQuery.data?.tests.pagination.length}
        />
      </Container>
    </DashboardPage>
  )
}

export default UnverifiedTestsList
